if (!customElements.get('facet-filters-form')) {
  class FacetFiltersForm extends HTMLElement {
    constructor() {
      super();
      this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

      this.debouncedOnSubmit = debounce((event) => {
        this.onSubmitHandler(event);
      }, 800);

      const facetForm = this.querySelector('form');
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

      // Bind change for sort by dropdown
      facetForm.addEventListener('change', (event) => {
        if (event.target.tagName === 'SELECT' && event.target.classList.contains('facet-filters__sort')) {
          this.debouncedOnSubmit(event);
        }
      });

      const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
      if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }

    static setListeners() {
      const onHistoryChange = (event) => {
        const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
        if (searchParams === FacetFiltersForm.searchParamsPrev) return;
        FacetFiltersForm.renderPage(searchParams, null, false);
      };
      window.addEventListener('popstate', onHistoryChange);
    }

    static savedScrollY = 0;
    static restoredFromBFCache = false;
    static initBFCacheScrollHandling() {
      // Capture scroll position before leaving
      window.addEventListener('pagehide', () => {
        FacetFiltersForm.savedScrollY = window.scrollY;
      });

      // Restore scroll after bfcache restore
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;

        FacetFiltersForm.restoredFromBFCache = true;

        // Disable smooth scrolling (important on iOS)
        const root = document.documentElement;
        const previousScrollBehavior = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';

        // Wait for layout + async DOM mutations
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, FacetFiltersForm.savedScrollY);
            root.style.scrollBehavior = previousScrollBehavior;
          });
        });
      });
    }

    static toggleActiveFacets(disable = true) {
      document.querySelectorAll('.js-facet-remove').forEach((element) => {
        element.classList.toggle('disabled', disable);
      });
    }

    static renderPage(searchParams, event, updateURLHash = true) {
      FacetFiltersForm.searchParamsPrev = searchParams;
      const sections = FacetFiltersForm.getSections();
      const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
      loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden', 'spinning-complete'));
      document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
      document.querySelectorAll('.js-product-count').forEach((countElem) => countElem.classList.add('loading'));

      sections.forEach((section) => {
        const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
        const filterDataUrl = (element) => element.url === url;

        FacetFiltersForm.filterData.some(filterDataUrl)
          ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
          : FacetFiltersForm.renderSectionFromFetch(url, event);
      });

      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    }

    static scrollToResults() {
      // Scroll to the results
      const results = document.getElementById('product-grid').closest('.section__inner');
      if (results.getBoundingClientRect().top < 0) {
        const headerHeightValue = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
        let headerHeightNum = headerHeightValue ? parseFloat(headerHeightValue) : 0;
        window.scrollTo({
          top: results.getBoundingClientRect().top + window.scrollY - headerHeightNum,
          behavior: 'smooth'
        });
      }
    }

    static renderSectionFromFetch(url, event) {
      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          const html = responseText;
          FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {html, url}];
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderProductCount(html);
          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
          window.renderSelects();
          window.loadDesktopOnlyTemplates();
          document.querySelectorAll('facet-filters-form custom-accordion').forEach(accordion => accordion.reinit());

          // Reinit infinite scroll
          const infiniteScroll = document.querySelector('infinite-scroll');
          if (infiniteScroll && typeof infiniteScroll.destroy === 'function') {
            infiniteScroll.destroy();
            infiniteScroll.init();
          }
        });

      FacetFiltersForm.scrollToResults();
    }

    static renderSectionFromCache(filterDataUrl, event) {
      const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      window.renderSelects();
      window.loadDesktopOnlyTemplates();
      document.querySelectorAll('facet-filters-form custom-accordion').forEach(accordion => accordion.reinit());
      FacetFiltersForm.scrollToResults();
    }

    // static renderProductGridContainer(html) {
    //   document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
    //     .parseFromString(html, 'text/html')
    //     .getElementById('ProductGridContainer').innerHTML;

    //   document
    //     .getElementById('ProductGridContainer')
    //     .querySelectorAll('.scroll-trigger')
    //     .forEach((element) => {
    //       element.classList.add('scroll-trigger--cancel');
    //     });
    // }

    static renderProductGridContainer(html) {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = parsedHTML.getElementById('ProductGridContainer');
      const currentGrid = document.getElementById('ProductGridContainer');

      if (!newGrid || !currentGrid) return;

      currentGrid.innerHTML = newGrid.innerHTML;

      currentGrid
        .querySelectorAll('.scroll-trigger')
        .forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });
    }

    static renderProductCount(html) {
      const count = new DOMParser().parseFromString(html, 'text/html').querySelector('.js-product-count')?.innerHTML;
      if (count) {
        document.querySelectorAll('.js-product-count').forEach((countElem) => {
          countElem.innerHTML = count;
          countElem.classList.remove('loading');
        });
      }

      const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden', 'spinning-complete'));
    }

    static renderFilters(html, event) {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

      const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
      const facetDetailsElementsFromDom = document.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');

      // If the server returned no filters at all (e.g. an impossible price range produced zero results),
      // keep the current filters on the page so the customer can still adjust them.
      if (facetDetailsElementsFromFetch.length === 0 && facetDetailsElementsFromDom.length > 0) {
        FacetFiltersForm.renderActiveFacets(parsedHTML);
        return;
      }

      // Remove facets that are no longer returned from the server
      Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
        if (!Array.from(facetDetailsElementsFromFetch).some(({id}) => currentElement.id === id)) {
          currentElement.remove();
        }
      });

      const matchesId = (element) => {
        const jsFilter = event ? event.target.closest('.js-filter') : undefined;
        return jsFilter ? element.id === jsFilter.id : false;
      };
      const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
      const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

      facetsToRender.forEach((elementToRender, index) => {
        const currentElement = document.getElementById(elementToRender.id);
        // Element already rendered in the DOM so just update the innerHTML
        if (currentElement) {
          const accordionContents = document.getElementById(elementToRender.id).querySelector('.custom-accordion__panel');
          if (accordionContents) {
            // Replace just the accordion content where possible
            accordionContents.innerHTML = elementToRender.querySelector('.custom-accordion__panel').innerHTML;
          } else {
            document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
          }
        } else {
          if (index > 0) {
            const {className: previousElementClassName, id: previousElementId} = facetsToRender[index - 1];
            // Same facet type (eg horizontal/vertical or drawer/mobile)
            if (elementToRender.className === previousElementClassName) {
              document.getElementById(previousElementId).after(elementToRender);
              return;
            }
          }

          if (elementToRender.parentElement) {
            document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
          }
        }
      });

      FacetFiltersForm.renderActiveFacets(parsedHTML);
      FacetFiltersForm.renderAdditionalElements(parsedHTML);

      if (countsToRender) {
        const closestJSFilterID = event.target.closest('.js-filter').id;

        if (closestJSFilterID) {
          FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        }
      }
    }

    static renderActiveFacets(html) {
      const newFacets = html.querySelector('.active-facets');
      if (newFacets) {
        document.querySelectorAll('.active-facets').forEach(pageFacets => {
          pageFacets.innerHTML = newFacets.innerHTML;
        });
      }
      FacetFiltersForm.toggleActiveFacets(false);
    }

    static renderAdditionalElements(html) {
      const mobileElementSelectors = ['.mobile-facets__open', '.js-apply-button', '.sorting'];

      mobileElementSelectors.forEach((selector) => {
        if (!html.querySelector(selector)) return;
        document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
      });

      document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
    }

    static renderCounts(source, target) {
      const targetSummary = target.querySelector('.facets__summary');
      const sourceSummary = source.querySelector('.facets__summary');

      if (sourceSummary && targetSummary) {
        targetSummary.outerHTML = sourceSummary.outerHTML;
      }

      const targetHeaderElement = target.querySelector('.facets__header');
      const sourceHeaderElement = source.querySelector('.facets__header');

      if (sourceHeaderElement && targetHeaderElement) {
        targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
      }

      const targetWrapElement = target.querySelector('.facets-wrap');
      const sourceWrapElement = source.querySelector('.facets-wrap');

      if (sourceWrapElement && targetWrapElement) {
        const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
        if (isShowingMore) {
          sourceWrapElement
            .querySelectorAll('.facets__item.hidden')
            .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
        }

        targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
      }
    }

    static updateURLHash(searchParams) {
      history.pushState({searchParams}, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }

    static getSections() {
      return [
        {
          section: document.getElementById('product-grid').dataset.id,
        },
      ];
    }

    // createSearchParams(form, isMobile) {
    //   const formData = new FormData(form);

    //   // Delete the mobile on sort_by param if desktop
    //   if (form.id === 'FacetFiltersForm' && isMobile === false && formData.has('sort_by') && form.classList.contains('facets__form--vertical')) {
    //     formData.delete('sort_by');
    //   }

    //   // Drop empty values (e.g. an unset price "From"/"To" or a blank select) so they are never sent as
    //   // empty filter params - Shopify treats those as active filters and can omit facets from the response.
    //   // Price inputs sitting at their default bounds (0 / range max) are not an active price filter either.
    //   const params = new URLSearchParams();
    //   formData.forEach((value, key) => {
    //     if (typeof value === 'string' && value.trim() === '') return;
    //     const input = form.querySelector(`.js-price-min[name="${key}"], .js-price-max[name="${key}"]`);
    //     if (input && input.dataset.default !== undefined && String(value) === input.dataset.default) return;
    //     params.append(key, value);
    //   });
    //   return params.toString();
    // }


//  createSearchParams(form, isMobile) {
//   const formData = new FormData(form);

//   // Don't submit sort_by twice on desktop vertical filters
//   if (
//     form.id === 'FacetFiltersForm' &&
//     isMobile === false &&
//     formData.has('sort_by') &&
//     form.classList.contains('facets__form--vertical')
//   ) {
//     formData.delete('sort_by');
//   }

//   const params = new URLSearchParams();

//   const priceMinInput = form.querySelector('.js-price-min');
//   const priceMaxInput = form.querySelector('.js-price-max');

//   formData.forEach((value, key) => {
//     if (typeof value === 'string' && value.trim() === '') {
//       return;
//     }

//     if (priceMinInput && key === priceMinInput.name) {
//       const price = parseFloat(value);

//       if (Number.isNaN(price)) {
//         return;
//       }

//       // Round to whole currency unit
//       const roundedPrice = Math.round(price);

//       // 0 means no minimum filter
//       if (roundedPrice <= 0) {
//         return;
//       }

//       params.append(key, String(roundedPrice));
//       return;
//     }
//     if (priceMaxInput && key === priceMaxInput.name) {
//       const price = parseFloat(value);

//       if (Number.isNaN(price)) {
//         return;
//       }

//       const defaultMax = parseFloat(
//         priceMaxInput.dataset.default || '0'
//       );

//       const roundedPrice = Math.round(price);

//       // Don't submit the default maximum value
//       if (roundedPrice >= Math.round(defaultMax)) {
//         return;
//       }

//       params.append(key, String(roundedPrice));
//       return;
//     }

//     // All other filters
//     params.append(key, value);
//   });

//   return params.toString();
// }


createSearchParams(form, isMobile) {
  const formData = new FormData(form);
  const params = new URLSearchParams();

  const isSortChange = form.querySelector('.facet-filters__sort') &&
    event?.target?.classList.contains('facet-filters__sort');


  if (
    form.id === 'FacetFiltersForm' &&
    isMobile === false &&
    formData.has('sort_by') &&
    form.classList.contains('facets__form--vertical')
  ) {
    formData.delete('sort_by');
  }


  const priceMinInput = form.querySelector('.js-price-min');
  const priceMaxInput = form.querySelector('.js-price-max');


  formData.forEach((value, key) => {

    // Ignore empty values
    if (!value || value.trim() === '') {
      return;
    }


    /*
      IMPORTANT:
      If user changes sorting,
      remove price filters completely
    */
    if (
      key === 'filter.v.price.gte' ||
      key === 'filter.v.price.lte'
    ) {

      if (isSortChange) {
        return;
      }


      const price = Math.round(Number(value));

      if (Number.isNaN(price)) {
        return;
      }


      // Remove default minimum
      if (
        key === priceMinInput?.name &&
        price <= 0
      ) {
        return;
      }


      // Remove default maximum
      if (
        key === priceMaxInput?.name &&
        price >= Number(priceMaxInput.dataset.max)
      ) {
        return;
      }


      params.append(key, price);
      return;
    }


    params.append(key,value);

  });


  return params.toString();
}
    onSubmitForm(searchParams, event) {
      FacetFiltersForm.renderPage(searchParams, event);
    }

    onSubmitHandler(event) {
      // const noJsElements = document.querySelectorAll('.no-js-list');
      // noJsElements.forEach((el) => el.remove());

      event.preventDefault();
      const sortFilterForms = document.querySelectorAll('facet-filters-form form');
      const thisForm = event.target.closest('form');

      if (thisForm) {
        if (thisForm.classList.contains('mobile-facets')) {
          const searchParams = this.createSearchParams(thisForm);
          this.onSubmitForm(searchParams, event);
        } else {
          const forms = [];
          const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

          sortFilterForms.forEach((form) => {
            if (!isMobile) {
              if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm' ||
                (form.id === 'FacetFiltersFormMobile' && document.querySelector('.facets-container-drawer'))) {
                forms.push(this.createSearchParams(form, isMobile));
              }
            } else if (form.id === 'FacetFiltersFormMobile') {
              forms.push(this.createSearchParams(form));
            }
          });
          this.onSubmitForm(forms.join('&'), event);
        }
      }
    }

    onActiveFilterClick(event) {
      event.preventDefault();
      FacetFiltersForm.toggleActiveFacets();
      const url =
        event.currentTarget.href.indexOf('?') == -1
          ? ''
          : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
      FacetFiltersForm.renderPage(url);
    }
  }

  FacetFiltersForm.filterData = [];
  FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
  FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
  customElements.define('facet-filters-form', FacetFiltersForm);
  FacetFiltersForm.initBFCacheScrollHandling();
  FacetFiltersForm.setListeners();
}

// if (!customElements.get('price-range')) {
//   class PriceRange extends HTMLElement {
//     constructor() {
//       super();
//       this.timer = null;
//       this.setMinAndMaxValues();
//       this.updateBarFill();
//       this.addEventListener('input', this.onInputUpdate.bind(this));
//       // Clamp typed values as soon as the field is committed (blur / enter), before the form submits
//       this.addEventListener('change', (event) => {
//         clearTimeout(this.timer);
//         this.setInputValue(event);
//         this.updateBarFill();
//       });
//     }

//     onInputUpdate(event) {
//       // A typed value above the allowed maximum is clamped immediately so it can never be submitted
//       const isNumberInput = event.target === this.priceMinElem || event.target === this.priceMaxElem;
//       if (isNumberInput && parseInt(event.target.value, 10) > this.maxValue) {
//         clearTimeout(this.timer);
//         this.setInputValue(event);
//         this.updateBarFill();
//         return;
//       }

//       if (event.detail !== undefined) {
//         clearTimeout(this.timer);
//         this.timer = setTimeout(() => {
//           this.setInputValue(event);
//           this.updateBarFill();
//         }, 600);
//       } else {
//         this.setInputValue(event);
//         this.updateBarFill();
//       }
//     }

//     setInputValue(event) {
//       // let rangeValue = parseInt(event.target.value, 10);
//       let rangeValue = parseInt(event.target.value);

//       if (event.target === this.minPriceRangeElem) {
//         rangeValue = Math.min(rangeValue, parseInt(this.maxPriceRangeElem.value, 10) - 1);
//         // rangeValue = Math.max(this.minValue, Math.round(rangeValue));
//         rangeValue = Math.max(this.minValue, rangeValue);
//       } else if (event.target === this.maxPriceRangeElem) {
//         rangeValue = Math.max(rangeValue, parseInt(this.minPriceRangeElem.value, 10) + 1);
//         // rangeValue = Math.min(this.maxValue, Math.round(rangeValue));
//         rangeValue = Math.min(this.maxValue, rangeValue);
//       } else if (event.target === this.priceMinElem) {
//         // Typed "From" value: empty means unset, otherwise keep it between 0 and ("To" or range max) - 1
//         if (event.target.value === '' || Number.isNaN(rangeValue)) rangeValue = this.minValue;
//         const upper = this.priceMaxElem.value !== '' ? parseInt(this.priceMaxElem.value, 10) : this.maxValue;
//         rangeValue = Math.max(this.minValue, Math.min(rangeValue, upper - 1));
//       } else if (event.target === this.priceMaxElem) {
//         // Typed "To" value: empty means unset, otherwise keep it between ("From" or 0) + 1 and range max
//         if (event.target.value === '' || Number.isNaN(rangeValue)) rangeValue = this.maxValue;
//         const lower = this.priceMinElem.value !== '' ? parseInt(this.priceMinElem.value, 10) : this.minValue;
//         rangeValue = Math.min(this.maxValue, Math.max(rangeValue, lower + 1));
//       }

//       if (event.target === this.minPriceRangeElem) {
//         this.priceMinElem.value = rangeValue;
//       } else if (event.target === this.maxPriceRangeElem) {
//         this.priceMaxElem.value = rangeValue;
//       }

//       event.target.value = rangeValue;

//       // If the number input has been typed
//       if (event.target === this.priceMinElem) this.minPriceRangeElem.value = event.target.value;
//       if (event.target === this.priceMaxElem) this.maxPriceRangeElem.value = event.target.value;

//       this.syncBounds();
//     }

//     // Keep the min/max attributes of the number inputs in step with each other
//     syncBounds() {
//       this.priceMinElem.setAttribute('max', this.priceMaxElem.value !== '' ? this.priceMaxElem.value : this.maxValue);
//       this.priceMaxElem.setAttribute('min', this.priceMinElem.value !== '' ? this.priceMinElem.value : this.minValue);
//     }

//     updateBarFill() {
//       const lowerValue = parseInt(this.priceMinElem.value || 0, 10);
//       const upperValue = parseInt(this.priceMaxElem.value || this.maxValue, 10);
//       const percentageLower = (lowerValue / this.maxValue) * 100;
//       const percentageUpper = (upperValue / this.maxValue) * 100;
//       this.querySelector('.price-range').style.setProperty('--range-gap-start', `${percentageLower}%`);
//       this.querySelector('.price-range').style.setProperty('--range-gap-end', `${100 - percentageUpper}%`);

//       // When both thumbs are pushed to the right edge the "max" thumb (later in the DOM) covers the "min" one,
//       // so raise whichever thumb is at the edge to keep it draggable.
//       const bothAtRightEdge = percentageUpper >= 99 && percentageLower >= 95;
//       const bothAtLeftEdge = percentageLower <= 1 && percentageUpper <= 5;
//       this.minPriceRangeElem.classList.toggle('price-range__input--on-top', bothAtRightEdge);
//       this.maxPriceRangeElem.classList.toggle('price-range__input--on-top', bothAtLeftEdge);

//       if (percentageLower === 0 && (100 - percentageUpper) === 0) {
//         this.classList.add('facets__price--unset');
//         this.parentElement.querySelector('facet-remove a')?.setAttribute('tabindex', '-1');
//       } else {
//         this.classList.remove('facets__price--unset');
//         this.parentElement.querySelector('facet-remove a')?.setAttribute('tabindex', '0');
//       }
//     }

//     setMinAndMaxValues() {
//       this.priceMinElem = this.querySelector('.js-price-min');
//       this.priceMaxElem = this.querySelector('.js-price-max');
//       this.minPriceRangeElem = this.querySelector('.js-price-range-min');
//       this.maxPriceRangeElem = this.querySelector('.js-price-range-max');

//       this.minValue = parseInt(this.priceMinElem.min, 10);
//       this.maxValue = parseInt(this.priceMaxElem.max, 10);

//       if (this.priceMaxElem.value) this.priceMinElem.setAttribute('max', this.priceMaxElem.value);
//       if (this.priceMinElem.value) this.priceMaxElem.setAttribute('min', this.priceMinElem.value);
//       if (this.priceMinElem.value === '') this.priceMaxElem.setAttribute('min', this.minValue);
//       if (this.priceMaxElem.value === '') this.priceMinElem.setAttribute('max', this.maxValue);
//     }
//   }

//   customElements.define('price-range', PriceRange);
// }


if (!customElements.get('price-range')) {
  class PriceRange extends HTMLElement {
    constructor() {
      super();

      this.minPriceElem = this.querySelector('.js-price-min');
      this.maxPriceElem = this.querySelector('.js-price-max');

      if (!this.minPriceElem || !this.maxPriceElem) return;

      this.minValue = Number(this.minPriceElem.min) || 0;
      this.maxValue = Number(this.maxPriceElem.max) || 0;

      this.handleInput = this.handleInput.bind(this);
      this.handleChange = this.handleChange.bind(this);

      // Validate while typing
      this.minPriceElem.addEventListener('input', this.handleInput);
      this.maxPriceElem.addEventListener('input', this.handleInput);

      // Final validation when value is committed
      this.minPriceElem.addEventListener('change', this.handleChange);
      this.maxPriceElem.addEventListener('change', this.handleChange);

      this.syncBounds();
    }

    handleInput(event) {
      const input = event.target;

      if (input.value === '') {
        return;
      }

      let value = Number(input.value);

      if (Number.isNaN(value)) {
        return;
      }

      // Don't allow a value greater than the highest price
      if (value > this.maxValue) {
        value = this.maxValue;
        input.value = value;
      }

      // Don't allow negative values
      if (value < this.minValue) {
        value = this.minValue;
        input.value = value;
      }

      this.syncBounds();
    }

    handleChange(event) {
      const input = event.target;

      if (input.value === '') {
        return;
      }

      let value = Number(input.value);

      if (Number.isNaN(value)) {
        input.value = '';
        return;
      }

      // Maximum validation
      if (value > this.maxValue) {
        value = this.maxValue;
      }

      // Minimum validation
      if (value < this.minValue) {
        value = this.minValue;
      }

      input.value = Math.round(value);

      this.syncBounds();
    }

    syncBounds() {
      const minValue = Number(this.minPriceElem.value);
      const maxValue = Number(this.maxPriceElem.value);

      /*
       * From cannot be greater than To
       */
      if (
        this.minPriceElem.value !== '' &&
        this.maxPriceElem.value !== '' &&
        minValue > maxValue
      ) {
        this.minPriceElem.value = maxValue;
      }

      /*
       * To cannot be lower than From
       */
      if (
        this.minPriceElem.value !== '' &&
        this.maxPriceElem.value !== '' &&
        Number(this.maxPriceElem.value) < Number(this.minPriceElem.value)
      ) {
        this.maxPriceElem.value = this.minPriceElem.value;
      }

      this.minPriceElem.setAttribute(
        'max',
        this.maxPriceElem.value !== ''
          ? this.maxPriceElem.value
          : this.maxValue
      );

      this.maxPriceElem.setAttribute(
        'min',
        this.minPriceElem.value !== ''
          ? this.minPriceElem.value
          : this.minValue
      );
    }
  }

  customElements.define('price-range', PriceRange);
}

if (!customElements.get('facet-remove')) {
  class FacetRemove extends HTMLElement {
    constructor() {
      super();
      const facetLink = this.querySelector('a');
      facetLink.setAttribute('role', 'button');
      facetLink.addEventListener('click', this.closeFilter.bind(this));
      facetLink.addEventListener('keyup', (event) => {
        event.preventDefault();
        if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
      });
    }

    closeFilter(event) {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
    }
  }

  customElements.define('facet-remove', FacetRemove);
}
